import type { GradientStop } from '@/constants/gradientPresets'
import {
  clampViewportAmbientIntensityScale,
  DEFAULT_LIGHTING_PRESET_ID,
  type LightingPresetId,
  isLightingPresetId,
} from '@/constants/lightingPresets'
import { type ResolutionPresetId, parseResolutionPresetId } from '@/constants/mockupDevices'
import { clampMaterialOpacity } from '@/animation/clampMaterialOpacity'
import { MIN_CLIP_DURATION_SEC } from '@/animation/timeline/clipConstraints'
import { normalizeClipToEndpointPair, syncAdjacentClipBoundaries } from '@/animation/timeline/clipEndpoints'
import { migrateKeyframesToClipsJson } from '@/project/migrateAnimationLayers'
import type { AnimationClip } from '@/types/animationClip'
import { isEasingPreset } from '@/types/easingPreset'
import { parseScreenImageTransitionModeId } from '@/constants/screenImageTransition'
import type { AnimationKeyframe, AnimationLayer, ScreenImageStackItem, ScreenMediaType } from '@/types/animationLayer'
import type { Vec3 } from '@/types/vec3'
import { createCompactRandomId } from '../../../shared/utils/randomUuid'

/** 与 `mockupEditor.backgroundMode` 一致，避免从 store 反向引用造成循环依赖 */
type BackgroundModeSnapshot = 'color' | 'gradient' | 'transparent' | 'image'

/** 工程 JSON 当前版本（v2：图层动画为 `clips`） */
export const PROJECT_SNAPSHOT_VERSION = 2 as const

/** v1 工程：图层含全局时间 `keyframes` */
export const PROJECT_SNAPSHOT_VERSION_V1 = 1 as const

/**
 * 可序列化的图层（不含运行时 `screenTextureSrc`）。
 */
export type AnimationLayerJson = {
  id: string
  name: string
  locked?: boolean
  deviceTemplateId: string
  position: Vec3
  rotationDeg: Vec3
  scale: Vec3
  screenMaterialFileName: string | null
  /** v2 起可选；缺省按 `screenMaterialFileName` 推断为 `image`，否则 `none` */
  screenMediaType?: ScreenMediaType
  screenVideoTimelineOffsetSec?: number
  /** 旧工程字段，加载时忽略 */
  screenVideoSyncMode?: string
  screenVideoFreezeTimeSec?: number
  /** v2 起可选：多图屏幕栈（仅存 `id` + `fileName`） */
  screenImages?: Array<{ id: string; fileName: string }>
  /** 多图栈栅格切换过渡模式（`-1`～`6`）；缺省加载后按淡入淡出 */
  screenImageTransitionMode?: number
  clips: AnimationClip[]
  /** 图层可见区间起点（秒） */
  visibilityStartTimeSec?: number
  /** 图层可见区间终点（秒） */
  visibilityEndTimeSec?: number
  /** 运行时字段（可选持久化，加载后会被按时间重新计算） */
  visible?: boolean
  /** 可见性扩展字段预留 */
  visibilityRuntime?: {
    fadeInSec?: number
    fadeOutSec?: number
  }
  textContent?: string
  textFontSize?: number
  textColor?: string
  textFontWeight?: 'normal' | 'bold'
  textAlign?: 'left' | 'center' | 'right'
}

/**
 * v1 磁盘图层（仅用于加载迁移）。
 */
type AnimationLayerJsonV1 = Omit<AnimationLayerJson, 'clips'> & {
  keyframes: AnimationKeyframe[]
}

/**
 * 保存到磁盘的工程快照（与 `mockupEditor` 核心字段对齐）。
 */
export type ProjectSnapshot = {
  version: typeof PROJECT_SNAPSHOT_VERSION
  sceneWidth: number
  sceneHeight: number
  /** 导出图/视频分辨率档（1080P / 2K / 4K），与预览画布尺寸独立 */
  exportResolutionPreset: ResolutionPresetId
  backgroundMode: BackgroundModeSnapshot
  backgroundHex: string
  /**
   * 背景图片原始文件名。实际图像数据（blob URL）不进入工程 JSON，加载工程时用户需重新上传。
   * 空字符串或未设置时视为 `null`。
   */
  backgroundImageFileName: string | null
  gradientStops: GradientStop[]
  activeGradientPresetId: string | null
  totalDurationSeconds: number
  currentTimeSeconds: number
  timelineGranularity: number
  /** 视口灯光 / IBL 预设 id */
  lightingPresetId: LightingPresetId
  /**
   * 半球光 + IBL `environmentIntensity` 的全局倍率（旧工程缺省由加载端补默认值）。
   */
  viewportAmbientIntensityScale: number
  animationLayers: AnimationLayerJson[]
}

type ProjectSnapshotV1Disk = Omit<ProjectSnapshot, 'version' | 'animationLayers'> & {
  version: typeof PROJECT_SNAPSHOT_VERSION_V1
  animationLayers: AnimationLayerJsonV1[]
}

function createScreenImageStackItemId(): string {
  return createCompactRandomId('si')
}

function inferScreenMediaTypeFromJson(row: AnimationLayerJson): ScreenMediaType {
  if (Array.isArray(row.screenImages) && row.screenImages.length > 0) return 'image'
  const explicit = row.screenMediaType
  if (explicit === 'none' || explicit === 'image' || explicit === 'video') return explicit
  const fn = row.screenMaterialFileName
  if (!fn) return 'none'
  const lower = fn.toLowerCase()
  if (lower.endsWith('.mp4') || lower.endsWith('.webm') || lower.endsWith('.mov')) return 'video'
  return 'image'
}

/**
 * 从 JSON 恢复 `screenImages`；兼容旧工程仅 `screenMaterialFileName` 的单图字段。
 */
function screenImagesFromJson(row: AnimationLayerJson, mediaType: ScreenMediaType): ScreenImageStackItem[] {
  if (mediaType === 'video') return []
  const raw = row.screenImages
  if (Array.isArray(raw) && raw.length > 0) {
    return raw
      .filter((it) => it && typeof it.fileName === 'string' && it.fileName.length > 0)
      .map((it) => ({
        id: typeof it.id === 'string' && it.id.length > 0 ? it.id : createScreenImageStackItemId(),
        fileName: it.fileName,
        textureSrc: null,
      }))
  }
  const fn = row.screenMaterialFileName
  if (mediaType === 'image' && fn) {
    return [{ id: createScreenImageStackItemId(), fileName: fn, textureSrc: null }]
  }
  return []
}

/**
 * @param layer - 运行时图层
 * @returns 写入 JSON 的图层副本（去掉 blob URL）
 */
export function animationLayerToJson(layer: AnimationLayer): AnimationLayerJson {
  return {
    id: layer.id,
    name: layer.name,
    locked: layer.locked,
    deviceTemplateId: layer.deviceTemplateId,
    position: { ...layer.position },
    rotationDeg: { ...layer.rotationDeg },
    scale: { ...layer.scale },
    screenMaterialFileName: layer.screenMaterialFileName,
    screenMediaType: layer.screenMediaType,
    screenVideoTimelineOffsetSec: layer.screenVideoTimelineOffsetSec,
    screenImages:
      layer.screenMediaType === 'image' && layer.screenImages.length > 0
        ? layer.screenImages.map((it) => ({ id: it.id, fileName: it.fileName }))
        : undefined,
    screenImageTransitionMode: layer.screenImageTransitionMode,
    visibilityStartTimeSec: layer.visibilityStartTimeSec,
    visibilityEndTimeSec: layer.visibilityEndTimeSec,
    visible: layer.visible,
    visibilityRuntime: layer.visibilityRuntime,
    textContent: layer.textContent,
    textFontSize: layer.textFontSize,
    textColor: layer.textColor,
    textFontWeight: layer.textFontWeight,
    textAlign: layer.textAlign,
    clips: layer.clips.map((c) => ({
      id: c.id,
      startSec: c.startSec,
      durationSec: c.durationSec,
      easingPreset: c.easingPreset,
      keyframes: c.keyframes.map((k) => ({
        id: k.id,
        timeInClip: k.timeInClip,
        position: { ...k.position },
        rotationDeg: { ...k.rotationDeg },
        scale: { ...k.scale },
        materialOpacity: clampMaterialOpacity(k.materialOpacity),
        screenImageId: k.screenImageId,
      })),
    })),
  }
}

/**
 * @param row - JSON 中的图层
 * @returns 运行时图层（屏幕贴图需用户重新上传）
 */
export function animationLayerFromJson(row: AnimationLayerJson): AnimationLayer {
  const mediaType = inferScreenMediaTypeFromJson(row)
  const screenImages = screenImagesFromJson(row, mediaType)
  const off = Number(row.screenVideoTimelineOffsetSec)
  const startRaw = Number(row.visibilityStartTimeSec)
  const endRaw = Number(row.visibilityEndTimeSec)
  const fallbackEnd = 10
  const start = Number.isFinite(startRaw) ? Math.max(0, startRaw) : 0
  const endBase = Number.isFinite(endRaw) ? Math.max(0, endRaw) : fallbackEnd
  const visStart = Math.min(start, endBase)
  const visEnd = Math.max(start, endBase)
  const layer: AnimationLayer = {
    id: row.id,
    name: row.name,
    locked: row.locked === true,
    deviceTemplateId: row.deviceTemplateId,
    position: { ...row.position },
    rotationDeg: { ...row.rotationDeg },
    scale: { ...row.scale },
    screenMaterialFileName:
      mediaType === 'image' && screenImages.length > 0 ? screenImages[0]!.fileName : row.screenMaterialFileName,
    screenTextureSrc: null,
    screenMediaType: mediaType,
    screenVideoTimelineOffsetSec: Number.isFinite(off) ? off : 0,
    screenImages,
    screenImageTransitionMode: parseScreenImageTransitionModeId(row.screenImageTransitionMode),
    visibilityStartTimeSec: visStart,
    visibilityEndTimeSec: visEnd,
    visible: row.visible ?? true,
    visibilityRuntime: row.visibilityRuntime
      ? {
          fadeInSec: Number.isFinite(Number(row.visibilityRuntime.fadeInSec))
            ? Number(row.visibilityRuntime.fadeInSec)
            : undefined,
          fadeOutSec: Number.isFinite(Number(row.visibilityRuntime.fadeOutSec))
            ? Number(row.visibilityRuntime.fadeOutSec)
            : undefined,
        }
      : undefined,
    textContent: typeof row.textContent === 'string' ? row.textContent : undefined,
    textFontSize: Number.isFinite(Number(row.textFontSize)) ? Number(row.textFontSize) : undefined,
    textColor: typeof row.textColor === 'string' ? row.textColor : undefined,
    textFontWeight: row.textFontWeight === 'normal' || row.textFontWeight === 'bold' ? row.textFontWeight : undefined,
    textAlign: row.textAlign === 'left' || row.textAlign === 'center' || row.textAlign === 'right' ? row.textAlign : undefined,
    clips: row.clips.map((c) => {
      const durationSec = Math.max(MIN_CLIP_DURATION_SEC, Number(c.durationSec) || MIN_CLIP_DURATION_SEC)
      const clip: AnimationClip = {
        id: c.id,
        startSec: c.startSec,
        durationSec,
        easingPreset: isEasingPreset(c.easingPreset) ? c.easingPreset : 'linear',
        keyframes: c.keyframes.map((k) => ({
          id: k.id,
          timeInClip: k.timeInClip,
          position: { ...k.position },
          rotationDeg: { ...k.rotationDeg },
          scale: { ...k.scale },
          materialOpacity: clampMaterialOpacity(k.materialOpacity),
          screenImageId: k.screenImageId,
        })),
      }
      normalizeClipToEndpointPair(clip)
      return clip
    }),
  }
  syncAdjacentClipBoundaries(layer)
  return layer
}

/**
 * 将磁盘 JSON 规范为当前 `ProjectSnapshot`（迁移 v1 → v2）。
 *
 * @param raw - `JSON.parse` 结果
 */
export function normalizeProjectSnapshot(raw: unknown): ProjectSnapshot | null {
  if (!raw || typeof raw !== 'object') return null
  const o = raw as Record<string, unknown>
  const version = o.version
  if (version !== PROJECT_SNAPSHOT_VERSION && version !== PROJECT_SNAPSHOT_VERSION_V1) return null

  const base = {
    sceneWidth: Number(o.sceneWidth) || 1920,
    sceneHeight: Number(o.sceneHeight) || 1080,
    exportResolutionPreset: parseResolutionPresetId(o.exportResolutionPreset),
    backgroundMode: (o.backgroundMode as BackgroundModeSnapshot) || 'color',
    backgroundHex: typeof o.backgroundHex === 'string' ? o.backgroundHex : '#ffffff',
    backgroundImageFileName:
      typeof o.backgroundImageFileName === 'string' && o.backgroundImageFileName.length > 0
        ? o.backgroundImageFileName
        : null,
    gradientStops: Array.isArray(o.gradientStops) ? (o.gradientStops as GradientStop[]) : [],
    activeGradientPresetId:
      o.activeGradientPresetId === null || typeof o.activeGradientPresetId === 'string'
        ? (o.activeGradientPresetId as string | null)
        : null,
    totalDurationSeconds: Math.min(600, Math.max(1, Math.round(Number(o.totalDurationSeconds) || 10))),
    currentTimeSeconds: Math.max(0, Number(o.currentTimeSeconds) || 0),
    timelineGranularity: Math.min(100, Math.max(0, Math.round(Number(o.timelineGranularity) || 0))),
    lightingPresetId: isLightingPresetId(String(o.lightingPresetId))
      ? (o.lightingPresetId as LightingPresetId)
      : DEFAULT_LIGHTING_PRESET_ID,
    viewportAmbientIntensityScale: clampViewportAmbientIntensityScale(o.viewportAmbientIntensityScale),
  }

  const layersRaw = o.animationLayers
  if (!Array.isArray(layersRaw)) return null

  if (version === PROJECT_SNAPSHOT_VERSION_V1) {
    const v1 = raw as ProjectSnapshotV1Disk
    const animationLayers: AnimationLayerJson[] = v1.animationLayers.map((row) => {
      const { keyframes, ...rest } = row
      const clips = migrateKeyframesToClipsJson(keyframes ?? [], base.totalDurationSeconds)
      return {
        ...(rest as Omit<AnimationLayerJson, 'clips'>),
        locked: false,
        clips,
      }
    })
    return {
      version: PROJECT_SNAPSHOT_VERSION,
      ...base,
      animationLayers,
    }
  }

  const animationLayers: AnimationLayerJson[] = (layersRaw as AnimationLayerJson[]).map((row) => {
    if (row && Array.isArray(row.clips)) {
      return {
        ...(row as AnimationLayerJson),
        locked: row.locked === true,
      }
    }
    /** 容错：缺 clips 时按空片段补一条整轨 clip */
    const partial = row as unknown as AnimationLayerJsonV1
    const clips = migrateKeyframesToClipsJson(partial.keyframes ?? [], base.totalDurationSeconds)
    const { keyframes: _k, ...rest } = partial as AnimationLayerJsonV1 & { keyframes?: AnimationKeyframe[] }
    void _k
    return { ...(rest as Omit<AnimationLayerJson, 'clips'>), locked: false, clips }
  })

  return {
    version: PROJECT_SNAPSHOT_VERSION,
    ...base,
    animationLayers,
  }
}

/** @deprecated 使用 {@link ProjectSnapshot} */
export type ProjectSnapshotV1 = ProjectSnapshot
